import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import {
  signIn as cognitoSignIn,
  signUp as cognitoSignUp,
  signOut as cognitoSignOut,
  verifyEmail as cognitoVerify,
  resendVerificationCode,
  getCurrentUser,
} from '../api/auth';

interface AuthUser {
  userId: string;
  email: string;
  displayName: string;
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, displayName: string, inviteCode?: string) => Promise<void>;
  verify: (email: string, code: string) => Promise<void>;
  resendCode: (email: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Check for existing session on mount
  useEffect(() => {
    getCurrentUser()
      .then((u) => setUser(u))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    await cognitoSignIn(email, password);
    const u = await getCurrentUser();
    setUser(u);
  }, []);

  const signUp = useCallback(
    async (email: string, password: string, displayName: string, inviteCode?: string) => {
      await cognitoSignUp(email, password, displayName, inviteCode);
    },
    [],
  );

  const verify = useCallback(async (email: string, code: string) => {
    await cognitoVerify(email, code);
  }, []);

  const resendCode = useCallback(async (email: string) => {
    await resendVerificationCode(email);
  }, []);

  const logout = useCallback(() => {
    cognitoSignOut();
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, loading, signIn, signUp, verify, resendCode, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
